// src/components/ClassSummaryPane.jsx
// 出席記録（集計部分）【2026-09-04 / 2026-09-11 見やすさ改修】
//
// 元は /class-summary の独立ページ。出席記録のページでは、この集計の下に
// レポート出力と出席CSV取り込みが続けて並ぶ。
// 授業の選択は親ページの授業名セレクトが担う。
//
// 「学生別の出席状況」は受講者名簿の出欠表と重複するので、ここには置かない。
// ここが受け持つのは全体集計と、回ごとのサマリ・授業メモ。
//
// 【2026-09-11 の改修】「パッと見で集計画面に見えない」ことへの対応。
//   1. 平均出席率を主役にした。数字が出ないときは「-」で黙らず、理由を書く。
//      履修者0人だと分母が0になって NULL が返る（classSummaryController）。
//      これは不具合ではなく名簿が未取込なだけなので、そう言って導線を出す。
//   2. 回ごとのサマリを表にした。1回75pxの縦並びだと8回で画面が埋まり、
//      どの回が悪かったのか比べられなかった。
//   3. 出力（この画面の成果物）への導線を上に出した。以前は下までスクロール
//      しないとレポート出力が見えなかった。
//
// 【2026-09-11 追加】この画面の主役はレポート出力のプレビューなので、
//   全体集計のすぐ下に children（レポート出力・CSV取り込み）を差し込み、
//   回ごとのサマリはその下へ回す。データ取得は1回のままにしたいので、
//   ペインを分割せず children を受け取る形にした。
//   全体集計の補助数値カード4枚は、すぐ下の1行と同じことを言っていたので畳んだ。

import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

// 回をまとめるときの扱い【2026-09-28】
const CONFLICT_LABEL = {
  keep:   '残す回の記録を使う',
  better: '良い方を使う（出席＞遅刻＞早退＞欠席）',
  worse:  '悪い方を使う（どちらかで遅刻なら遅刻）',
};
const SINGLE_LABEL = {
  move: '残す回へ移す（どれかに記録があれば出席）',
  drop: '移さない（まとめる回だけの記録は捨てる）',
  both: '全部の回にいた学生だけ残す',
};

/** 時刻を 'HH:MM'（端末の時刻）にする。読めなければ空 */
const fmtClock = (v) => {
  if (!v) return '';
  const x = new Date(v);
  if (isNaN(x.getTime())) return '';
  return String(x.getHours()).padStart(2, '0') + ':' + String(x.getMinutes()).padStart(2, '0');
};

const STATUS_LABEL = { active: '進行中', completed: '終了', cancelled: '休講' };
const STATUS_COLOR = { active: '#2563eb', completed: '#4b5563', cancelled: '#6b7280' };
// 授業日を 'YYYY-MM-DD'（端末の日付）にする【2026-09-28】
//   サーバーは DATE を '2026-09-27T15:00:00.000Z'（= JST の 9/28）で返すので、
//   先頭10文字を切ると1日前になっていた。
const fmtDate = (d) => {
  if (!d) return '-';
  const str = String(d);
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
  const x = new Date(str);
  if (isNaN(x.getTime())) return str.slice(0, 10);
  const p = (n) => String(n).padStart(2, '0');
  return `${x.getFullYear()}-${p(x.getMonth() + 1)}-${p(x.getDate())}`;
};

/** 出席率から帯の色を決める（低いほど赤寄り） */
function rateColor(rate) {
  if (rate == null) return '#d1d5db';
  if (rate >= 80) return '#16a34a';
  if (rate >= 60) return '#d97706';
  return '#dc2626';
}

export default function ClassSummaryPane({ classId, setErr, setMsg, children }) {
  const [data, setData]   = useState(null);
  const [busy, setBusy]   = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  // 回をまとめる（出席記録の統合）【2026-09-28】
  const [picked, setPicked] = useState([]);        // 選んだ回の id
  const [keepId, setKeepId] = useState(null);      // 残す回
  const [merging, setMerging] = useState(false);
  const [conflict, setConflict] = useState('keep');   // 両方に記録がある学生: keep / better / worse
  const [single, setSingle]     = useState('move');   // 片方にしか無い学生: move / drop / both
  const [preview, setPreview]   = useState(null);     // 下見の件数
  const navigate = useNavigate();

  const auth = () => ({
    Authorization: 'Bearer ' + localStorage.getItem('token'),
    'Content-Type': 'application/json'
  });

  const load = async () => {
    setBusy(true);
    try {
      const res = await fetch('/api/class-summary/' + classId, { headers: auth() });
      const d   = await res.json();
      if (!res.ok) throw new Error(d.error || ('HTTP ' + res.status));
      setData(d);
    } catch (e) { setErr(e.message); setData(null); }
    finally     { setBusy(false); }
  };

  useEffect(() => { load(); }, [classId]); // eslint-disable-line

  const togglePick = (id) => {
    setPicked(p => (p.includes(id) ? p.filter(x => x !== id) : [...p, id]));
  };

  // まとめたらどうなるかを下見する（サーバーで実際に流して巻き戻す）【2026-09-28】
  useEffect(() => {
    setPreview(null);
    if (!data || picked.length < 2) return undefined;
    const rows = data.sessions.filter(x => picked.includes(x.id));
    const keep = rows.find(x => x.id === keepId)
      || [...rows].sort((a, b) => Number(b.attended_count) - Number(a.attended_count))[0];
    let alive = true;
    const t = setTimeout(async () => {
      try {
        const r = await fetch('/api/class-summary/sessions/merge', {
          method: 'POST', headers: auth(), body: mergeBody(rows, keep, true),
        });
        const d = await r.json();
        if (alive) setPreview(d.success ? d : { error: d.message || 'まとめられません' });
      } catch (e) { if (alive) setPreview(null); }
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [picked, keepId, conflict, single, data]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * 選んだ回を1つにまとめる【2026-09-28】
   *   1回の授業が二度押しなどで2つ以上の回に分かれたときに使う。同じ日の回だけ。
   *   両方に記録がある学生は、残す回の記録を使う。空になった回は消える。
   */
  const mergeBody = (rows, keep, dryRun) => JSON.stringify({
    intoSessionId: keep.id, fromSessionIds: rows.filter(r => r.id !== keep.id).map(x => x.id),
    conflict, single, dryRun,
  });

  const mergePicked = async (rows, keep) => {
    const froms = rows.filter(r => r.id !== keep.id);
    const lines = froms.map(r => `・${fmtDate(r.session_date)} ${fmtClock(r.start_time)}（出席 ${r.attended_count}）`).join('\n');
    const how = `両方に記録がある学生: ${CONFLICT_LABEL[conflict]}\n片方にしか記録がない学生: ${SINGLE_LABEL[single]}`;
    const after = preview ? `\n\nまとめたあとの出席: ${preview.attended} 人` : '';
    if (!window.confirm(
      `次の回を「${fmtDate(keep.session_date)} ${fmtClock(keep.start_time)}（出席 ${keep.attended_count}）」にまとめます。\n\n${lines}\n\n`
      + how + after + '\n\nまとめた回は消えます。元には戻せません。\n\nまとめますか？')) return;
    setMerging(true);
    try {
      const r = await fetch('/api/class-summary/sessions/merge', {
        method: 'POST', headers: auth(), body: mergeBody(rows, keep, false),
      });
      const d = await r.json();
      if (!r.ok || !d.success) throw new Error(d.message || d.error || 'まとめられませんでした');
      if (setMsg) setMsg(d.message);
      if (setErr) setErr('');
      setPicked([]); setKeepId(null); setPreview(null);
      load();
    } catch (e) { setErr(e.message); }
    finally { setMerging(false); }
  };

  /**
   * 1回ぶんの授業を出席記録ごと消す（授業そのものは残す）【2026-09-28】
   *   1回目で件数を聞き、画面で了承が取れたら同じ件数を送り返して実行する。
   */
  const deleteSession = async (s) => {
    const label = fmtDate(s.session_date);
    const send = (confirmRecords) => fetch('/api/class-summary/sessions/' + s.id, {
      method: 'DELETE', headers: auth(),
      ...(confirmRecords === undefined ? {} : { body: JSON.stringify({ confirmRecords }) }),
    });
    try {
      const r1 = await send();
      const d1 = await r1.json();
      if (d1.code !== 'CONFIRM_REQUIRED') throw new Error(d1.message || d1.error || '消せませんでした');
      const warn = d1.count > 0
        ? `${label} の回を消します。\n\nこの回の出席記録 ${d1.count} 件も一緒に消えます。\n授業そのものは残ります。元には戻せません。\n\n本当に消しますか？`
        : `${label} の回を消します。\n授業そのものは残ります。元には戻せません。\n\n本当に消しますか？`;
      if (!window.confirm(warn)) return;
      const r2 = await send(d1.count);
      const d2 = await r2.json();
      if (!r2.ok || !d2.success) throw new Error(d2.message || d2.error || '消せませんでした');
      if (setMsg) setMsg(d2.message || '消しました');
      if (setErr) setErr('');
      load();
    } catch (e) { setErr(e.message); }
  };

  /**
   * 出席した学生がいない授業を、この画面から消す【2026-09-28】
   *   サーバーは「出席（出席・遅刻・早退）が0件なら畳まずに消せる」。
   *   欠席だけの記録は件数を見せて、了承が取れたら同じ件数を送り返す（授業管理と同じ2段階）。
   */
  const deleteThisClass = async () => {
    const name = (data && data.class && data.class.name) || 'この授業';
    const send = (confirmRecords) => fetch('/api/schedule/classes/' + classId, {
      method: 'DELETE', headers: auth(),
      ...(confirmRecords === undefined ? {} : { body: JSON.stringify({ confirmRecords }) }),
    });
    try {
      const r1 = await send();
      const d1 = await r1.json();
      let done = d1;
      if (d1.code === 'CONFIRM_REQUIRED') {
        const warn = d1.count > 0
          ? `「${name}」を削除します。\n\n欠席の記録 ${d1.count} 件も一緒に消えます。\n元には戻せません。\n\n本当に削除しますか？`
          : `「${name}」を削除します。\n元には戻せません。\n\n本当に削除しますか？`;
        if (!window.confirm(warn)) return;
        const r2 = await send(d1.count);
        done = await r2.json();
        if (!r2.ok && !done.success) throw new Error(done.message || '削除に失敗しました');
      } else if (!r1.ok && !d1.success) {
        throw new Error(d1.message || '削除に失敗しました');
      }
      try {
        if (localStorage.getItem('lastClassId') === String(classId)) localStorage.removeItem('lastClassId');
      } catch (e) { /* 参照できなくても動く */ }
      if (setMsg) setMsg(done.message || '削除しました');
      navigate('/classes');
    } catch (e) { setErr(e.message); }
  };

  const saveNotes = async (sessionId) => {
    setSaving(true); setErr('');
    try {
      const res = await fetch('/api/class-summary/sessions/' + sessionId + '/notes', {
        method: 'PUT', headers: auth(), body: JSON.stringify({ notes: draft })
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || ('HTTP ' + res.status));
      setMsg('メモを保存しました');
      setEditingId(null);
      await load();
    } catch (e) { setErr(e.message); }
    finally     { setSaving(false); }
  };

  const goExport = () => {
    const el = document.getElementById('report-export');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (busy) return <div className="card"><p>読み込み中...</p></div>;
  if (!data) return <div className="card"><p style={{ color: '#6b7280' }}>まとめを取得できませんでした。</p></div>;

  const ov = data.overview || null;
  const enrolled = ov ? Number(ov.enrolled_students) : 0;
  const held     = ov ? Number(ov.total_sessions) - Number(ov.cancelled_sessions) : 0;
  const rate     = ov && ov.average_attendance_rate != null ? Number(ov.average_attendance_rate) : null;
  const marks    = ov
    ? Number(ov.present_total) + Number(ov.late_total) + Number(ov.early_leave_total)
    : 0;

  // 出席率が出せない理由。黙って「-」を出すと不具合に見える
  let blockedReason = null;
  if (rate == null) {
    if (enrolled === 0)   blockedReason = 'roster';
    else if (held === 0)  blockedReason = 'no-session';
    else                  blockedReason = 'unknown';
  }

  return (
    <>
      {ov && (
        <div className="card">
          <div className="card-header"
               style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
            <h2>全体集計</h2>
            <button className="btn-secondary" onClick={goExport} style={{ whiteSpace: 'nowrap' }}>
              書き出しへ ↓
            </button>
          </div>

          {/* 主役は平均出席率。ここが読めれば、この画面の用は半分足りる */}
          <div style={{
            display: 'flex', flexWrap: 'wrap', alignItems: 'center',
            gap: '1.5rem', marginBottom: 0
          }}>
            <div style={{ minWidth: 190 }}>
              <div className="stat-label">平均出席率</div>
              <div style={{
                fontSize: '2.2rem', fontWeight: 700, lineHeight: 1.1,
                color: rate == null ? '#6b7280' : rateColor(rate)
              }}>
                {rate == null ? '—' : rate}
                {rate != null && <span style={{ fontSize: '1.1rem', marginLeft: 2 }}>%</span>}
              </div>
            </div>
            <div style={{ flex: 1, minWidth: 220 }}>
              <div className="progress-bar">
                <div className="progress-fill"
                     style={{ width: (rate == null ? 0 : rate) + '%', background: rateColor(rate) }} />
              </div>
              <div style={{ fontSize: '0.82rem', color: '#6b7280', marginTop: '0.45rem',
                            display: 'flex', flexWrap: 'wrap', gap: '0.15rem 1rem' }}>
                <span>履修 <strong style={{ color: '#374151' }}>{enrolled}</strong> 人</span>
                <span>実施 <strong style={{ color: '#374151' }}>{held}</strong> 回
                  {Number(ov.cancelled_sessions) > 0 && '（休講 ' + ov.cancelled_sessions + ' 回）'}</span>
                <span>打刻 <strong style={{ color: '#374151' }}>{marks}</strong> 件</span>
                <span>遅刻 {ov.late_total} ・ 早退 {ov.early_leave_total}</span>
              </div>
            </div>
          </div>

          {blockedReason === 'roster' && (
            <div style={{
              background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8,
              padding: '0.7rem 0.9rem', marginTop: '0.9rem', fontSize: '0.9rem', color: '#92400e'
            }}>
              <strong>履修者が登録されていないため、出席率を計算できません。</strong>
              <div style={{ marginTop: '0.3rem' }}>
                出席率は「打刻数 ÷（実施回数 × 履修者数）」で出しています。分母の履修者が0人なので、
                数字そのものが作れません。
                <Link to={'/students?classId=' + classId} style={{ marginLeft: '0.4rem' }}>
                  学生管理で名簿を開く →
                </Link>
              </div>
            </div>
          )}
          {blockedReason === 'no-session' && (
            <div style={{
              background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8,
              padding: '0.7rem 0.9rem', marginTop: '0.9rem', fontSize: '0.9rem', color: '#4b5563'
            }}>
              実施済みの回がまだないので、出席率は出せません（0% ではありません）。
            </div>
          )}

          {/* 出席した学生がいない授業は、ここから消せる【2026-09-28】
              間違えて作った授業や、試しに受付を開いただけの授業の片付け用 */}
          {marks === 0 && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '0.8rem', flexWrap: 'wrap',
              background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8,
              padding: '0.7rem 0.9rem', marginTop: '0.9rem', fontSize: '0.9rem', color: '#7f1d1d'
            }}>
              <span style={{ flex: 1, minWidth: 220 }}>
                出席した学生がいない授業です（全員欠席、または記録なし）。
                不要な授業なら、ここから削除できます。
              </span>
              <button className="btn-danger" onClick={deleteThisClass} style={{ whiteSpace: 'nowrap' }}>
                この授業を削除
              </button>
            </div>
          )}

        </div>
      )}

      {/* レポート出力（プレビュー）と CSV 取り込み。ここが画面の主役 */}
      {children}

      <div className="card">
        <div className="card-header"><h2>回ごとのサマリ・授業メモ</h2></div>

        {data.sessions.length === 0 ? (
          <p style={{ color: '#6b7280' }}>まだ授業を実施していません。</p>
        ) : (
          <>
          {/* 回をまとめる【2026-09-28】左のチェックで2つ以上選ぶと出る */}
          {(() => {
            const rows = data.sessions.filter(x => picked.includes(x.id));
            if (rows.length === 0) {
              return (
                <p style={{ fontSize: '0.8rem', color: '#6b7280', margin: '0 0 0.4rem' }}>
                  1回の授業が2つに分かれてしまったときは、左のチェックで選ぶと1つにまとめられます（同じ日の回だけ）。
                </p>
              );
            }
            const keep = rows.find(x => x.id === keepId)
              || [...rows].sort((a, b) => Number(b.attended_count) - Number(a.attended_count))[0];
            const days = new Set(rows.map(x => fmtDate(x.session_date)));
            const active = rows.filter(x => x.status === 'active' && x.id !== keep.id);
            const blocked = rows.length < 2 ? '2つ以上選んでください'
              : days.size > 1 ? '日付が違う回はまとめられません（同じ日の回だけです）'
              : active.length ? '受付中の回は、残す回にしか選べません'
              : null;
            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap',
                            background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8,
                            padding: '0.5rem 0.7rem', marginBottom: '0.5rem', fontSize: '0.88rem' }}>
                <strong>{rows.length} 回を選択中</strong>
                {rows.length >= 2 && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                    残す回
                    <select value={keep.id} onChange={e => setKeepId(Number(e.target.value))}>
                      {rows.map(x => (
                        <option key={x.id} value={x.id}>
                          {fmtDate(x.session_date)}
                          {fmtClock(x.start_time) ? ' ' + fmtClock(x.start_time) : ''}
                          {'（出席 ' + x.attended_count + '）'}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                {rows.length >= 2 && (
                  <>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                      両方に記録がある学生
                      <select value={conflict} onChange={e => setConflict(e.target.value)}>
                        {Object.entries(CONFLICT_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                      片方にしか記録がない学生
                      <select value={single} onChange={e => setSingle(e.target.value)}>
                        {Object.entries(SINGLE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                    </label>
                  </>
                )}
                <button className="btn-primary" disabled={!!blocked || merging || !!(preview && preview.error)}
                        onClick={() => mergePicked(rows, keep)}>
                  {merging ? 'まとめています…' : '1つにまとめる'}
                </button>
                <button className="btn-secondary" onClick={() => { setPicked([]); setKeepId(null); }}>選択を外す</button>
                {blocked && <span style={{ color: '#b45309' }}>{blocked}</span>}
                {!blocked && preview && (
                  <div style={{ width: '100%', fontSize: '0.82rem', color: preview.error ? '#b45309' : '#1e3a8a' }}>
                    {preview.error ? preview.error : (
                      <>まとめたあと: 出席 <strong>{preview.attended}</strong> 人
                        （移す {preview.moved}・重なり {preview.dropped}
                        {preview.changed ? `（うち書き換え ${preview.changed}）` : ''}
                        {preview.discarded ? `・捨てる ${preview.discarded}` : ''}
                        {preview.notInAll ? `・全部の回にいないため消す ${preview.notInAll} 人` : ''}）</>
                    )}
                  </div>
                )}
              </div>
            );
          })()}
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th title="まとめる回を選ぶ" />
                  <th style={{ whiteSpace: 'nowrap' }}>日付</th>
                  <th style={{ whiteSpace: 'nowrap' }}>状態</th>
                  <th style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>出席</th>
                  <th style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>遅刻</th>
                  <th style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>早退</th>
                  <th style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>欠席</th>
                  {/* 履修者名簿に無い学生の打刻【2026-09-14】分母には入れない */}
                  <th style={{ textAlign: 'right', whiteSpace: 'nowrap' }}
                      title="打刻はあるが、履修者名簿に載っていない学生の数">名簿未登録</th>
                  <th style={{ minWidth: 120 }}>出席率</th>
                  <th>メモ</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.sessions.map(s => {
                  const cancelled = s.status === 'cancelled';
                  const attended  = Number(s.attended_count);
                  const absent    = (!cancelled && enrolled > 0) ? Math.max(enrolled - attended, 0) : null;
                  const sRate     = (!cancelled && enrolled > 0)
                    ? Math.round(attended * 1000 / enrolled) / 10
                    : null;
                  const editing   = editingId === s.id;

                  return (
                    <React.Fragment key={s.id}>
                      <tr style={cancelled ? { color: '#6b7280' } : undefined}>
                        <td>
                          <input type="checkbox" checked={picked.includes(s.id)} disabled={cancelled}
                                 title={cancelled ? '休講の回はまとめられません' : 'まとめる回として選ぶ'}
                                 onChange={() => togglePick(s.id)} />
                        </td>
                        <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                          {fmtDate(s.session_date)}
                          {/* 同じ日に2つある回を見分けるため、始めた時刻も出す【2026-09-28】 */}
                          {fmtClock(s.start_time) && (
                            <span style={{ fontWeight: 400, color: '#6b7280', marginLeft: '0.35rem', fontSize: '0.82rem' }}>
                              {fmtClock(s.start_time)}
                            </span>
                          )}
                        </td>
                        <td style={{ color: STATUS_COLOR[s.status] || '#4b5563', whiteSpace: 'nowrap' }}>
                          {STATUS_LABEL[s.status] || s.status}
                        </td>
                        <td style={{ textAlign: 'right' }}>{cancelled ? '—' : attended}</td>
                        <td style={{ textAlign: 'right' }}>{cancelled ? '—' : s.late_count}</td>
                        <td style={{ textAlign: 'right' }}>{cancelled ? '—' : s.early_leave_count}</td>
                        <td style={{ textAlign: 'right' }}>{absent == null ? '—' : absent}</td>
                        <td style={{ textAlign: 'right',
                                     color: Number(s.unenrolled_count) > 0 ? '#9a3412' : undefined,
                                     fontWeight: Number(s.unenrolled_count) > 0 ? 700 : undefined }}>
                          {cancelled ? '—' : (Number(s.unenrolled_count) || 0)}
                        </td>
                        <td>
                          {sRate == null ? (
                            <span style={{ color: '#6b7280' }}>—</span>
                          ) : (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <div className="progress-bar" style={{ flex: 1, minWidth: 54 }}>
                                <div className="progress-fill"
                                     style={{ width: sRate + '%', background: rateColor(sRate) }} />
                              </div>
                              <span style={{ fontSize: '0.8rem', color: rateColor(sRate), minWidth: 42,
                                             textAlign: 'right' }}>{sRate}%</span>
                            </div>
                          )}
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <span style={{
                              flex: 1, color: s.lesson_notes ? '#374151' : '#6b7280',
                              fontSize: '0.85rem', overflow: 'hidden',
                              textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 240
                            }}>
                              {s.lesson_notes ? s.lesson_notes.replace(/\s+/g, ' ') : '（メモなし）'}
                            </span>
                            {!editing && (
                              <button className="btn-secondary"
                                      style={{ padding: '0.25rem 0.6rem', fontSize: '0.8rem',
                                               whiteSpace: 'nowrap' }}
                                      onClick={() => {
                                        setEditingId(s.id);
                                        setDraft(s.lesson_notes || '');
                                        setMsg('');
                                      }}>
                                {s.lesson_notes ? '編集' : '書く'}
                              </button>
                            )}
                          </div>
                        </td>
                        <td>
                          {/* この回だけ出席記録ごと消す【2026-09-28】受付中の回は消せない */}
                          {s.status !== 'active' && (
                            <button className="btn-danger"
                                    style={{ padding: '0.25rem 0.6rem', fontSize: '0.8rem', whiteSpace: 'nowrap' }}
                                    title="この回を出席記録ごと消します（授業そのものは残ります）"
                                    onClick={() => deleteSession(s)}>
                              消す
                            </button>
                          )}
                        </td>
                      </tr>

                      {editing && (
                        <tr>
                          {/* 「名簿外」列で 8 → 9【2026-09-14】、「消す」列で 9 → 10【2026-09-28】 */}
                          <td colSpan={11} style={{ background: '#f9fafb' }}>
                            <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.35rem' }}>
                              {fmtDate(s.session_date)} の授業メモ
                            </div>
                            <textarea value={draft} onChange={e => setDraft(e.target.value)}
                                      rows={4} maxLength={10000}
                                      style={{ width: '100%', boxSizing: 'border-box' }}
                                      placeholder="授業内容・連絡事項などを記録" />
                            <div style={{ marginTop: '0.4rem', display: 'flex', gap: '0.5rem',
                                          alignItems: 'center' }}>
                              <button className="btn-primary" disabled={saving}
                                      onClick={() => saveNotes(s.id)}>
                                {saving ? '保存中...' : '保存'}
                              </button>
                              <button className="btn-secondary" disabled={saving}
                                      onClick={() => setEditingId(null)}>
                                キャンセル
                              </button>
                              {s.lesson_notes_updated_at && (
                                <span style={{ color: '#6b7280', fontSize: '0.8rem' }}>
                                  更新: {String(s.lesson_notes_updated_at).slice(0, 16).replace('T', ' ')}
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>
    </>
  );
}
